/**
 * MyCopro - Residence Store
 * Inclut le double identifiant : id (technique) et residenceCode (client-facing)
 * ✅ CORRIGÉ: Chargement des champs bootstrap (hasActiveCouncil, setupMode, etc.)
 * ✅ CORRIGÉ: Interface Subscription alignée avec Cloud Functions (grace, plans small/medium/large)
 * ✅ CORRIGÉ: Fallback subscription = trial au lieu de active
 * ✅ MISE À JOUR: Ajout LabelFormat (format étiquettes BAL sauvegardé au niveau résidence)
 *   - Interface LabelFormat dans Residence
 *   - Action saveLabelFormat → Firestore residences/{id}/settings/labelFormat
 *   - Chargement auto du labelFormat au loadResidence/reloadResidence
 */

import { create } from 'zustand';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/config/firebase';
import type { Bailleur } from '@/types';
import type { LabelFormat } from '@/types/request';
import { DEFAULT_LABEL_FORMAT } from '@/types/request';
import { logger } from '@/utils/logger';

export type { Bailleur };

export interface Syndic {
  name: string;
  phone: string;
  email: string;
  hours?: string;
  address?: string;    // ✅ Adresse du cabinet syndic
  portalUrl?: string;  // ✅ NOUVEAU: URL du portail copropriétaire du syndic
}

export interface Gardien {
  name: string;
  phone?: string;
  email?: string;
  hours?: string;
  apartment?: string;
}

export interface ResidenceCodes {
  portail?: string;
  hall?: string;
  parking?: string;
}

export interface TrashSchedule {
  om?: string;
  recyclage?: string;
  verre?: string;
  encombrants?: string;
}

export interface ResidenceStats {
  apartments: number;
  owners: number;
  tenants: number;
}

export interface ChatSettings {
  residence: boolean;
  owners: boolean;
  council: boolean;
}

// ✅ CORRIGÉ: Subscription alignée avec Cloud Functions et residence.ts
export interface Subscription {
  status: 'active' | 'expiring' | 'expired' | 'trial' | 'grace';
  startDate?: string;
  endDate: string;
  plan?: 'small' | 'medium' | 'large' | 'xlarge' | null;
  autoRenew?: boolean;
  statusUpdatedAt?: string;
  lastReminderSent?: string;
  graceStartDate?: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
}

export interface Residence {
  id: string;
  residenceCode: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  country?: string;
  photo?: string | null;
  reference?: string;           // legacy — préférer syndicReference
  syndicReference?: string;     // ✅ Référence métier de la copropriété auprès du syndic (ex: SYN-2024-JRD-001)
  syndicId?: string;            // ID technique Firestore du syndic (ne pas afficher/modifier côté utilisateur)
  syndic?: Syndic;
  gardien?: Gardien | null;
  hasGardien?: boolean;
  codes?: ResidenceCodes;
  trash?: TrashSchedule;
  elevator?: string | null;
  stats?: ResidenceStats;
  chatSettings?: ChatSettings;
  subscription?: Subscription;
  bailleurs?: Bailleur[];
  createdAt?: Date;
  createdBy?: string;
  
  // ✅ Champs bootstrap
  setupMode?: 'bootstrap' | 'active';
  hasActiveCouncil?: boolean;
  bootstrapEndedAt?: string;
  firstCouncilMemberId?: string;
  memberCount?: number;
  bootstrapLimit?: number;

  // ✅ NOUVEAU: Format d'étiquettes BAL sauvegardé au niveau résidence
  // Chargé depuis Firestore: residences/{id}/settings/labelFormat
  // Partagé entre tous les membres CS/Syndic de la résidence
  labelFormat?: LabelFormat;

  // ✅ NOUVEAU: Email du conseil syndical (CC des emails envoyés au syndic)
  // Saisi dans la page Services, stocké dans residences/{id}.csEmail
  csEmail?: string | null;

  // ✅ NOUVEAU: Toggle réception email pour nouvelles demandes
  csEmailOnRequest?: boolean;

  // Paramètres d'accès du gardien configurables par le CS
  gardienSettings?: {
    canValidateInscriptions?: boolean;
    canValidateRequests?: boolean;
    canSendNotifications?: boolean;
    chatResidence?: boolean;
    chatOwners?: boolean;
    chatCouncil?: boolean;
  } | null;

  // Bâtiments — null/[] = résidence à bâtiment unique
  buildings?: string[] | null;
  // Codes d'accès spécifiques par bâtiment
  buildingCodes?: Record<string, { portail?: string | null; hall?: string | null; parking?: string | null }> | null;

  // Présence d'un bailleur social dans la résidence
  // null = non défini, true = oui, false = non (tous les locataires = bailleur privé)
  hasSocialBailleur?: boolean | null;

  // Conformité plan — géré par checkPlanCompliance CF
  planAlert?: {
    activeUsers: number;
    plan: 'small' | 'medium' | 'large';
    detectedAt: string;
    lastNotifiedAt: string;
  } | null;
  activeUsersCount?: number | null;
}

export interface ResidenceListItem {
  id: string;
  residenceCode: string;
  name: string;
  address: string;
  city: string;
}

const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

const ALLOWED_PHOTO_TYPES: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

export interface PhotoValidationResult {
  valid: boolean;
  error?: string;
}

export const validatePhoto = (file: File): PhotoValidationResult => {
  if (file.size > MAX_PHOTO_SIZE) {
    return { valid: false, error: `Photo trop volumineuse. Maximum : 5 MB` };
  }
  const mimeType = file.type.toLowerCase();
  if (!ALLOWED_PHOTO_TYPES[mimeType]) {
    return { valid: false, error: `Seuls les formats JPG et PNG sont acceptés.` };
  }
  return { valid: true };
};


// ✅ Fallback subscription par défaut = trial (pas active)
const DEFAULT_SUBSCRIPTION_FALLBACK: Subscription = {
  status: 'trial',
  endDate: ''
};

/**
 * Construit l'objet Residence à partir des données Firestore
 * Factorisé pour éviter la duplication entre loadResidence et reloadResidence
 */
function buildResidenceFromFirestore(docId: string, data: Record<string, any>): Residence {
  return {
    id: docId,
    residenceCode: data.residenceCode || '',
    name: data.name || '',
    address: data.address || '',
    postalCode: data.postalCode || '',
    city: data.city || '',
    country: data.country || 'France',
    photo: data.photo || null,
    // ✅ syndicReference = référence métier (ex: SYN-2024-JRD-001), lue depuis le champ dédié ou legacy reference
    syndicReference: data.syndicReference || data.reference || '',
    reference: data.reference || '',  // legacy
    syndicId: data.syndicId || '',    // ID technique Firestore — ne jamais afficher/modifier côté utilisateur
    syndic: data.syndic || { name: '', phone: '', email: '', hours: '' },
    gardien: data.gardien || null,
    hasGardien: data.hasGardien || false,
    codes: data.codes || { portail: '', hall: '', parking: '' },
    trash: data.trash || { om: '', recyclage: '', verre: '' },
    elevator: data.elevator || null,
    stats: data.stats || { apartments: 0, owners: 0, tenants: 0 },
    chatSettings: data.chatSettings || { residence: false, owners: false, council: false },
    // ✅ CORRIGÉ: Fallback = trial, pas active
    subscription: data.subscription || DEFAULT_SUBSCRIPTION_FALLBACK,
    bailleurs: data.bailleurs || [],
    hasSocialBailleur: data.hasSocialBailleur ?? null,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    createdBy: data.createdBy || '',
    // ✅ Champs bootstrap
    setupMode: data.setupMode || 'bootstrap',
    hasActiveCouncil: data.hasActiveCouncil === true,
    bootstrapEndedAt: data.bootstrapEndedAt || null,
    firstCouncilMemberId: data.firstCouncilMemberId || null,
    memberCount: data.memberCount || 0,
    bootstrapLimit: data.bootstrapLimit || 10,
    // ✅ NOUVEAU: labelFormat sera chargé séparément depuis la sous-collection settings
    labelFormat: undefined,
    // ✅ NOUVEAU: Email du conseil syndical (CC des emails envoyés au syndic)
    csEmail: data.csEmail || null,
    // ✅ NOUVEAU: Toggle réception email pour nouvelles demandes
    csEmailOnRequest: data.csEmailOnRequest === true,
    // Paramètres d'accès du gardien configurables par le CS
    gardienSettings: data.gardienSettings || null,
    // Bâtiments de la résidence (null = bâtiment unique)
    buildings: data.buildings || null,
    // Codes d'accès par bâtiment
    buildingCodes: data.buildingCodes || null,
    // Conformité plan
    planAlert: data.planAlert || null,
    activeUsersCount: data.activeUsersCount ?? null,
  };
}

/**
 * ✅ NOUVEAU: Charge le format d'étiquette depuis Firestore
 * Chemin: residences/{residenceId}/settings/labelFormat
 * Retourne DEFAULT_LABEL_FORMAT si le document n'existe pas encore
 */
async function loadLabelFormatFromFirestore(residenceId: string): Promise<LabelFormat> {
  try {
    const settingsRef = doc(db, 'residences', residenceId, 'settings', 'labelFormat');
    const snap = await getDoc(settingsRef);
    if (snap.exists()) {
      const data = snap.data();
      return {
        darkMode: data.darkMode ?? DEFAULT_LABEL_FORMAT.darkMode,
        textAlign: data.textAlign ?? DEFAULT_LABEL_FORMAT.textAlign,
        aptPosition: data.aptPosition ?? DEFAULT_LABEL_FORMAT.aptPosition,
        numDisplay: data.numDisplay ?? DEFAULT_LABEL_FORMAT.numDisplay,
        // ✅ NOUVEAU: Mode self-service (par défaut false si absent dans Firestore)
        selfService: data.selfService ?? DEFAULT_LABEL_FORMAT.selfService
      };
    }
  } catch (error) {
    logger.db(' Error loading labelFormat:', error);
  }
  return { ...DEFAULT_LABEL_FORMAT };
}

interface ResidenceState {
  currentResidence: Residence | null;
  residences: ResidenceListItem[];
  bailleurs: Bailleur[];
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  loadResidence: (residenceId: string) => Promise<void>;
  reloadResidence: (residenceId: string) => Promise<void>;
  setCurrentResidence: (residence: Residence | null) => void;
  setResidence: (residence: Residence | null) => void;
  setResidences: (residences: ResidenceListItem[]) => void;
  setBailleurs: (bailleurs: Bailleur[]) => void;
  addBailleur: (bailleur: Omit<Bailleur, 'id'>) => Promise<void>;
  updateBailleur: (id: string, data: Partial<Bailleur>) => Promise<void>;
  removeBailleur: (id: string) => Promise<void>;
  updateResidenceSettings: (settings: Partial<Residence>) => void;
  updateChatSettings: (settings: Partial<ChatSettings>) => void;
  uploadResidencePhoto: (residenceId: string, file: File) => Promise<{ success: boolean; error?: string }>;
  deleteResidencePhoto: (residenceId: string) => Promise<boolean>;
  // ✅ NOUVEAU: Gestion du format d'étiquette BAL
  saveLabelFormat: (format: LabelFormat) => Promise<boolean>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearResidence: () => void;
}

export const useResidenceStore = create<ResidenceState>((set, get) => ({
  currentResidence: null,
  residences: [],
  bailleurs: [],
  isLoading: false,
  isUploading: false,
  uploadProgress: 0,
  error: null,

  loadResidence: async (residenceId: string) => {
    if (!residenceId) {
      set({ currentResidence: null, isLoading: false });
      return;
    }
    const { currentResidence } = get();
    if (currentResidence?.id === residenceId) return;

    set({ isLoading: true, error: null });
    try {
      const residenceRef = doc(db, 'residences', residenceId);
      const residenceSnap = await getDoc(residenceRef);
      if (residenceSnap.exists()) {
        const data = residenceSnap.data();
        // ✅ Utilise la fonction factorisée
        const residence = buildResidenceFromFirestore(residenceSnap.id, data);
        
        // ✅ NOUVEAU: Charger le format d'étiquette depuis la sous-collection settings
        const labelFormat = await loadLabelFormatFromFirestore(residenceId);
        residence.labelFormat = labelFormat;
        
        set({ currentResidence: residence, bailleurs: residence.bailleurs || [], isLoading: false, error: null });
      } else {
        set({ currentResidence: null, isLoading: false, error: 'Résidence non trouvée' });
      }
    } catch (error) {
      console.error('Error loading residence:', error);
      set({ isLoading: false, error: 'Erreur lors du chargement' });
    }
  },

  reloadResidence: async (residenceId: string) => {
    if (!residenceId) return;
    set({ isLoading: true, error: null });
    try {
      const residenceRef = doc(db, 'residences', residenceId);
      const residenceSnap = await getDoc(residenceRef);
      if (residenceSnap.exists()) {
        const data = residenceSnap.data();
        // ✅ Utilise la fonction factorisée
        const residence = buildResidenceFromFirestore(residenceSnap.id, data);
        
        // ✅ NOUVEAU: Charger le format d'étiquette depuis la sous-collection settings
        const labelFormat = await loadLabelFormatFromFirestore(residenceId);
        residence.labelFormat = labelFormat;
        
        set({ currentResidence: residence, bailleurs: residence.bailleurs || [], isLoading: false, error: null });
      } else {
        set({ currentResidence: null, isLoading: false, error: 'Résidence non trouvée' });
      }
    } catch (error) {
      console.error('Error reloading residence:', error);
      set({ isLoading: false, error: 'Erreur lors du rechargement' });
    }
  },

  setCurrentResidence: (residence) => set({ currentResidence: residence }),
  setResidence: (residence) => set({ currentResidence: residence }),
  clearResidence: () => set({ currentResidence: null, bailleurs: [], error: null }),
  setResidences: (residences) => set({ residences }),
  setBailleurs: (bailleurs) => set({ bailleurs }),

  addBailleur: async (bailleurData) => {
    const { currentResidence, bailleurs } = get();
    if (!currentResidence?.id) return;
    const newBailleur: Bailleur = {
      ...bailleurData,
      id: `bailleur_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    const updatedBailleurs = [...bailleurs, newBailleur];
    set({ bailleurs: updatedBailleurs });
    try {
      await updateDoc(doc(db, 'residences', currentResidence.id), { bailleurs: updatedBailleurs });
    } catch (error) {
      console.error('Error adding bailleur:', error);
      set({ bailleurs });
    }
  },

  updateBailleur: async (id, data) => {
    const { currentResidence, bailleurs } = get();
    if (!currentResidence?.id) return;
    const updatedBailleurs = bailleurs.map((b) => b.id === id ? { ...b, ...data } : b);
    set({ bailleurs: updatedBailleurs });
    try {
      await updateDoc(doc(db, 'residences', currentResidence.id), { bailleurs: updatedBailleurs });
    } catch (error) {
      console.error('Error updating bailleur:', error);
      set({ bailleurs });
    }
  },

  removeBailleur: async (id) => {
    const { currentResidence, bailleurs } = get();
    if (!currentResidence?.id) return;
    const updatedBailleurs = bailleurs.filter((b) => b.id !== id);
    set({ bailleurs: updatedBailleurs });
    try {
      await updateDoc(doc(db, 'residences', currentResidence.id), { bailleurs: updatedBailleurs });
    } catch (error) {
      console.error('Error removing bailleur:', error);
      set({ bailleurs });
    }
  },

  updateResidenceSettings: (settings) => {
    const { currentResidence } = get();
    if (!currentResidence) return;
    set({ currentResidence: { ...currentResidence, ...settings } });
  },

  updateChatSettings: (settings) => {
    const { currentResidence } = get();
    if (!currentResidence) return;
    set({ currentResidence: { ...currentResidence, chatSettings: { ...currentResidence.chatSettings, ...settings } as ChatSettings } });
  },

  // ✅ NOUVEAU: Sauvegarder le format d'étiquette dans Firestore
  // Chemin: residences/{id}/settings/labelFormat
  // Accessible par tous les membres CS/Syndic de la résidence
  saveLabelFormat: async (format: LabelFormat): Promise<boolean> => {
    const { currentResidence } = get();
    if (!currentResidence?.id) return false;

    try {
      const settingsRef = doc(db, 'residences', currentResidence.id, 'settings', 'labelFormat');
      await setDoc(settingsRef, {
        darkMode: format.darkMode,
        textAlign: format.textAlign,
        aptPosition: format.aptPosition,
        numDisplay: format.numDisplay,
        // ✅ NOUVEAU: Mode self-service
        selfService: format.selfService ?? false,
        updatedAt: new Date().toISOString()
      });
      
      // Mettre à jour le store local
      set({
        currentResidence: {
          ...currentResidence,
          labelFormat: { ...format }
        }
      });

      logger.db(' Label format saved:', format);
      return true;
    } catch (error) {
      console.error('[ResidenceStore] Error saving label format:', error);
      return false;
    }
  },

  uploadResidencePhoto: async (residenceId, file) => {
    if (!residenceId) return { success: false, error: 'Résidence non trouvée' };
    const validation = validatePhoto(file);
    if (!validation.valid) return { success: false, error: validation.error };

    set({ isUploading: true, uploadProgress: 0 });
    try {
      // Supprimer l'ancienne photo si elle existe
      const residenceRef = doc(db, 'residences', residenceId);
      const residenceSnap = await getDoc(residenceRef);
      const oldStoragePath = residenceSnap.exists() ? residenceSnap.data().photoStoragePath : null;
      if (oldStoragePath) {
        try {
          await deleteObject(ref(storage, oldStoragePath));
        } catch (e) { logger.storage('Could not delete old photo:', e); }
      }
      set({ uploadProgress: 30 });

      const fileExtension = file.name.split('.').pop() || 'jpg';
      const storagePath = `residences/${residenceId}/photos/residence_photo_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      set({ uploadProgress: 70 });

      const photoUrl = await getDownloadURL(storageRef);
      const photoUpdatedAt = new Date().toISOString();
      await updateDoc(residenceRef, { photo: photoUrl, photoStoragePath: storagePath, photoUpdatedAt });
      set({ uploadProgress: 90 });

      const { currentResidence } = get();
      if (currentResidence?.id === residenceId) {
        set({ currentResidence: { ...currentResidence, photo: photoUrl } });
      }
      set({ uploadProgress: 100 });
      return { success: true };
    } catch (error) {
      console.error('Error uploading photo:', error);
      return { success: false, error: "Erreur lors de l'upload" };
    } finally {
      set({ isUploading: false, uploadProgress: 0 });
    }
  },

  deleteResidencePhoto: async (residenceId) => {
    if (!residenceId) return false;
    try {
      const residenceRef = doc(db, 'residences', residenceId);
      const residenceSnap = await getDoc(residenceRef);
      if (residenceSnap.exists()) {
        const data = residenceSnap.data();
        if (data.photoStoragePath) {
          try {
            await deleteObject(ref(storage, data.photoStoragePath));
          } catch (e) { logger.storage('Could not delete photo:', e); }
        }
        await updateDoc(residenceRef, { photo: null, photoStoragePath: null, photoUpdatedAt: null });
        const { currentResidence } = get();
        if (currentResidence?.id === residenceId) {
          set({ currentResidence: { ...currentResidence, photo: null } });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting photo:', error);
      return false;
    }
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
}));

export const selectCurrentResidence = (state: ResidenceState) => state.currentResidence;
export const selectResidences = (state: ResidenceState) => state.residences;
export const selectBailleurs = (state: ResidenceState) => state.bailleurs;
export const selectResidenceCode = (state: ResidenceState) => state.currentResidence?.residenceCode;
export const selectChatSettings = (state: ResidenceState) => state.currentResidence?.chatSettings;
export const selectSubscription = (state: ResidenceState) => state.currentResidence?.subscription;
// ✅ NOUVEAU: Sélecteur du format d'étiquette (avec fallback sur les valeurs par défaut)
export const selectLabelFormat = (state: ResidenceState) => state.currentResidence?.labelFormat || DEFAULT_LABEL_FORMAT;

export default useResidenceStore;
